import React, { useContext } from 'react';
import { Autocomplete, Stack } from '@mui/material';
import _ from 'lodash';
import { DependencyOption } from '../../tasks/StartCondition';
import { EmailTemplateStartCondition } from '../../../types/EmailTemplate';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import DateOffsetRow from '../DateOffsetRow/DateOffsetRow';
import TextFieldWithTooltip from '../TextFieldWithTooltip/TextFieldWithTooltip';

export interface IEmailStartConditionProps<T> {
  emailTemplate: T;
  setEmailTemplate: (template: T) => void;
  dependencyOptions: DependencyOption[];
  disabledTriggers?: EmailTemplateStartCondition[];
}

const TRIGGER_OPTIONS: { label: string; value: EmailTemplateStartCondition }[] = [
  { label: 'No trigger', value: EmailTemplateStartCondition.MANUAL },
  { label: 'Days from event', value: EmailTemplateStartCondition.DATE_OFFSET },
  { label: 'On event completion', value: EmailTemplateStartCondition.EVENT_DEPENDENCY },
  { label: 'On task completion', value: EmailTemplateStartCondition.TASK_DEPENDENCY },
];

export default function EmailStartCondition<
  T extends {
    offset: number | null;
    depends_on_uuid?: string | null;
    start_condition: EmailTemplateStartCondition;
    uuid?: string | null;
  },
>({ emailTemplate, setEmailTemplate, dependencyOptions, disabledTriggers = [] }: IEmailStartConditionProps<T>) {
  const { disabled } = useContext(DisableContext);

  const handleTriggerTypeChange = (value: EmailTemplateStartCondition | undefined) => {
    if (!value) return;

    // reset old dependencies and offsets
    const reset: Pick<T, 'offset' | 'depends_on_uuid'> = {
      offset: null,
      depends_on_uuid: null,
    };
    const updated = { ...emailTemplate, ...reset, start_condition: value };

    setEmailTemplate(updated);
  };

  const availableOptions = _.reject(TRIGGER_OPTIONS, (opt) => _.includes(disabledTriggers, opt.value));

  // exclude self task from dependency options
  const dependencyTasks = _.reject(dependencyOptions, { uuid: emailTemplate.uuid || '' });

  return (
    <Stack spacing={2}>
      <Autocomplete
        options={availableOptions}
        onChange={(_, option) => handleTriggerTypeChange(option?.value)}
        getOptionLabel={(option) => option.label}
        value={_.find(availableOptions, { value: emailTemplate.start_condition }) || null}
        disabled={disabled}
        fullWidth
        size="small"
        renderInput={(params) => <TextFieldWithTooltip {...params} label="Send Trigger" required tooltip="Choose when this email should be sent." />}
      />

      {emailTemplate.start_condition === EmailTemplateStartCondition.DATE_OFFSET && (
        <DateOffsetRow
          label="Days from event"
          prefix="Send"
          tooltip="Set how many days before or after the event this email sends."
          value={emailTemplate.offset}
          onChange={(v) => setEmailTemplate({ ...emailTemplate, offset: v })}
        />
      )}

      {emailTemplate.start_condition === EmailTemplateStartCondition.TASK_DEPENDENCY && (
        <Autocomplete<DependencyOption>
          fullWidth
          size="small"
          options={dependencyTasks}
          getOptionLabel={(opt) => opt.name}
          value={_.find(dependencyTasks, { uuid: emailTemplate.depends_on_uuid || '' }) || null}
          onChange={(_, opt) => setEmailTemplate({ ...emailTemplate, depends_on_uuid: opt?.uuid ?? null })}
          renderInput={(params) => <TextFieldWithTooltip {...params} label="After task" tooltip="Select the task that triggers this email once completed." />}
          disabled={disabled}
        />
      )}
    </Stack>
  );
}
